import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { IonContent, IonPopover } from "@ionic/react";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/expert/ExpertJourneysList.module.css";
import ExpertRoleBoxes from "@components/ExpertRoleBoxes";
import ArchiveJourneyForm from "@components/forms/ArchiveJourneyForm";
import ExpertAssignmentForm from "@components/forms/ExpertAssignmentForm";
import ExpertJourneysFilters from "@components/forms/ExpertJourneysFilters";
import NewJourneyForm from "@components/forms/NewJourneyForm";
import HandleQuery from "@components/HandleQuery";
import JourneyArchiveModal from "@components/JourneyArchiveModal";
import JourneyStatusModal from "@components/JourneyStatusModal";
import Modal, { ModalElement } from "@components/Modal";
import Nbsp from "@components/Nbsp";
import Table, { TableHeader } from "@components/Table";
import TableHeaderExtraInfo from "@components/TableHeaderExtraInfo";
import Icon from "@components/text/Icon";
import Link from "@components/text/Link";
import PageTitle from "@components/text/PageTitle";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  ClientAccessTypeEnum,
  ClientRoleRelationshipEnum,
  ExpertGlobalRoleEnum,
  ExpertJourneysExpertRolesEnum,
  ExpertJourneysSortByEnum,
  ExpertJourneysStatusEnum,
  ExpertRoleRoleEnum,
  GetExpertDocument,
  GetExpertJourneysDocument,
  GetExpertJourneysQuery,
  GetExpertJourneysQueryVariables,
  GetExpertJourneySummariesDocument,
  GetExpertJourneySummariesQuery,
  GetExpertJourneySummariesQueryVariables,
  GetExpertQuery,
  GetExpertQueryVariables,
  JourneyExpertArchivedToggleDocument,
  JourneyExpertArchivedToggleMutation,
  JourneyExpertArchivedToggleMutationVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { countryNameForCode } from "@utils/countryUtils";
import { handleTrapFocus } from "@utils/keyboardUtils";
import { getCareProfileName, hasBasicPackage } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { timezoneAbbreviation } from "@utils/timeUtils";

type Filters = ComponentProps<typeof ExpertJourneysFilters>["filters"];

type Journey = NonNullable<NonNullable<NonNullable<GetExpertJourneysQuery["expertJourneys"]["edges"]>[number]>["node"]>;

type PopoverState = {
  event: Event | undefined;
  journey: Journey | null;
  open: boolean;
};

type Props = {
  allJourneys?: boolean;
  expertId?: string;
  myJourneys?: boolean;
};

type UnreadCountsByJourneyId = { [key: string]: number };

const ExpertJourneysList = ({ allJourneys = false, expertId, myJourneys = false }: Props) => {
  const intl = useIntl();
  const statusDefinitionOpener = useOpener();
  const archiveDefinitionOpener = useOpener();
  const archiveModalOpener = useOpener();
  const newJourneyModalOpener = useOpener();
  const expertAssignmentOpener = useOpener();
  const archiveModalRef = useRef<ModalElement>(null);
  const expertAssignmentModalRef = useRef<ModalElement>(null);
  const newJourneyModalRef = useRef<ModalElement>(null);
  const apolloClient = useApolloClient();
  const myExpert = useMyExpert();

  const [sortBy, setSortBy] = useState<ExpertJourneysSortByEnum>(ExpertJourneysSortByEnum.created_at);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.descending);

  const [popoverState, setPopoverState] = useState<PopoverState>({ event: undefined, journey: null, open: false });

  const [modifyingJourney, setModifyingJourney] = useState<Journey>();
  const [newJourneyClientId, setNewJourneyClientId] = useState<string>();

  const [unreadCountsByJourneyId, setUnreadCountsByJourneyId] = useState<UnreadCountsByJourneyId>({});

  const journeyPopoverRef = useRef<HTMLDivElement>(null);

  const [getExpertJourneys, getExpertJourneysQuery] = useLazyQuery<
    GetExpertJourneysQuery,
    GetExpertJourneysQueryVariables
  >(GetExpertJourneysDocument);

  const getExpertJourneySummaries = useLazyQueryWithPromise<
    GetExpertJourneySummariesQuery,
    GetExpertJourneySummariesQueryVariables
  >(GetExpertJourneySummariesDocument);

  const updateSummaries = useCallback(() => {
    const summaryExpertId = expertId ?? myExpert?.id;
    const summaryJourneyIds = getExpertJourneysQuery.data?.expertJourneys.edges?.map(j => j?.node?.id);

    if (summaryExpertId && summaryJourneyIds) {
      getExpertJourneySummaries({
        fetchPolicy: "network-only",
        variables: { expertId: summaryExpertId, journeyIds: summaryJourneyIds as string[] }
      }).then(summaries => {
        const newMap: UnreadCountsByJourneyId = {};
        summaries.data.expertJourneySummaries.forEach(summary => {
          newMap[summary.journeyId] = summary.unreadMessageCount;
        });
        if (!equal(newMap, unreadCountsByJourneyId)) {
          setUnreadCountsByJourneyId(newMap);
        }
      });
    }
  }, [
    expertId,
    getExpertJourneySummaries,
    getExpertJourneysQuery.data?.expertJourneys.edges,
    myExpert?.id,
    unreadCountsByJourneyId
  ]);

  useSubscribeToJourneys(() => {
    // This kicks out all of the expertJourneys queries so we will re-make them. If you're flipping between filters
    // then the queries related to them will be cached, but if a journey is changed then we need to redo those queries.
    apolloClient.cache.evict({ fieldName: "expertJourneys", id: "ROOT_QUERY" });

    getExpertJourneysQuery.refetch();
    updateSummaries();
  });

  useEffect(() => {
    updateSummaries();
  }, [updateSummaries]);

  const getExpertQuery = useQuery<GetExpertQuery, GetExpertQueryVariables>(GetExpertDocument, {
    variables: { id: expertId ?? "" }
  });

  const [journeyExpertArchivedToggle] = useMutationWithValidation<
    JourneyExpertArchivedToggleMutation,
    JourneyExpertArchivedToggleMutationVariables
  >(JourneyExpertArchivedToggleDocument, "expert");

  const [filters, setFilters] = useState<Filters>({
    employerId: "",
    expertArchived: false,
    primaryExpertId: "",
    role: ExpertJourneysExpertRolesEnum.all,
    search: "",
    selfPaid: false,
    statuses: [ExpertJourneysStatusEnum.active]
  });

  useEffect(() => {
    if (expertId) {
      getExpertJourneys({
        variables: {
          allJourneys,
          expertId,
          ...filters,
          sortBy,
          sortDirection
        }
      });
    }
  }, [allJourneys, expertId, filters, getExpertJourneys, sortBy, sortDirection]);

  const journeys = useNodesFromConnection(getExpertJourneysQuery.data?.expertJourneys);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const handleExpertArchivedClick = useCallback(() => {
    if (popoverState.journey?.expertArchived) {
      journeyExpertArchivedToggle({ expertArchived: false, journeyId: popoverState.journey.id });
    } else {
      if (popoverState.journey) {
        setModifyingJourney(popoverState.journey);
        archiveModalOpener.open();
      }
    }
    setPopoverState({ ...popoverState, open: false });
  }, [archiveModalOpener, journeyExpertArchivedToggle, popoverState]);

  const handleJourneyReassign = useCallback(() => {
    if (popoverState.journey) {
      setModifyingJourney(popoverState.journey);
      expertAssignmentOpener.open();
    }
    setPopoverState({ ...popoverState, open: false });
  }, [expertAssignmentOpener, popoverState]);

  const handleAddJourneyClick = useCallback(() => {
    if (popoverState.journey) {
      setModifyingJourney(popoverState.journey);
    }

    if (popoverState.journey?.owner.id) {
      setNewJourneyClientId(popoverState.journey.owner.id);
      newJourneyModalOpener.open();
    }
    setPopoverState({ ...popoverState, open: false });
  }, [newJourneyModalOpener, popoverState]);

  const handleArchiveError = useCallback(() => {
    if (archiveModalRef.current) {
      archiveModalRef.current.scrollToTop();
    }
  }, [archiveModalRef]);

  const handleAssignmentError = useCallback(() => {
    if (expertAssignmentModalRef.current) {
      expertAssignmentModalRef.current.scrollToTop();
    }
  }, [expertAssignmentModalRef]);

  const handleNewJourneyError = useCallback(() => {
    if (newJourneyModalRef.current) {
      newJourneyModalRef.current.scrollToTop();
    }
  }, [newJourneyModalRef]);

  const journeyOpenedAt = useCallback(
    (journey: Journey) =>
      journey.expertRoles?.find(role =>
        allJourneys ? role.role === ExpertRoleRoleEnum.primary : role.expert.id === expertId
      )?.openedAt,
    [allJourneys, expertId]
  );

  const clientHasUnattachedCarePlanningPackage = useMemo(
    () =>
      modifyingJourney?.owner.eligiblePackageGrants.some(
        grant => grant.hasCarePlanning && !grant.carePlanningJourneyId
      ),
    [modifyingJourney?.owner.eligiblePackageGrants]
  );

  const tableHeaders: TableHeader<ExpertJourneysSortByEnum>[] = [
    { columnSortBy: ExpertJourneysSortByEnum.unread_message_count, icon: "comments" },
    { columnSortBy: ExpertJourneysSortByEnum.new },
    { columnSortBy: ExpertJourneysSortByEnum.client_name },
    { columnSortBy: ExpertJourneysSortByEnum.loved_one_name },
    {
      columnSortBy: ExpertJourneysSortByEnum.status,
      extraInfo: (
        <TableHeaderExtraInfo
          linkKey="pages.expert.journeys.seeStatusDefinitions"
          onOpen={statusDefinitionOpener.open}
          titleKey="pages.expert.journeys.status"
        />
      )
    },
    { columnSortBy: ExpertJourneysSortByEnum.last_expert_contact },
    { columnSortBy: ExpertJourneysSortByEnum.last_client_contact },
    { columnSortBy: ExpertJourneysSortByEnum.created_at },
    { columnSortBy: ExpertJourneysSortByEnum.care_plan_last_updated },
    {
      columnSortBy: ExpertJourneysSortByEnum.role,
      messageKey: myJourneys
        ? "pages.expert.journeys.tableHeadings.myRole"
        : "pages.expert.journeys.tableHeadings.expertRole",
      messageValues: { name: getExpertQuery.data?.expert.firstName }
    },
    { messageKey: "pages.expert.journeys.tableHeadings.actions" }
  ];

  const pageTitle = useMemo(() => {
    if (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager) {
      if (myExpert?.id === getExpertQuery.data?.expert.id && !allJourneys) {
        return intl.formatMessage({
          id: "pages.expert.journeys.expertsJourneyList"
        });
      }
      return intl.formatMessage(
        {
          id: allJourneys ? "pages.expert.journeys.allJourneyList" : "pages.expert.journeys.expertsManagerJourneyList"
        },
        {
          expertName: getExpertQuery.data?.expert.fullName ?? ""
        }
      );
    }
    return intl.formatMessage({
      id: allJourneys ? "pages.expert.journeys.allJourneyList" : "pages.expert.journeys.expertsJourneyList"
    });
  }, [
    allJourneys,
    getExpertQuery.data?.expert.fullName,
    getExpertQuery.data?.expert.id,
    intl,
    myExpert?.expertGlobalRole,
    myExpert?.id
  ]);

  return (
    <>
      <div
        onKeyUp={event =>
          handleTrapFocus(event, journeyPopoverRef, () =>
            setPopoverState({ event: undefined, journey: null, open: false })
          )
        }
        ref={journeyPopoverRef}
      >
        <IonPopover
          className={styles.popover}
          event={popoverState.event}
          isOpen={popoverState.open}
          onDidDismiss={() => setPopoverState({ event: undefined, journey: null, open: false })}
        >
          <div className={styles.popoverList}>
            <div className={styles.popoverItem}>
              <InternalLink
                className="ion-text-center"
                to={journeyRoute({
                  journeyId: popoverState.journey?.id ?? "",
                  linkAfterJourney: "/schedule-consult",
                  myExpert
                })}
              >
                <FormattedMessage id="pages.expert.journeys.popoverTitles.scheduleConsult" />
              </InternalLink>
            </div>
            <div className={styles.popoverItem}>
              <InternalLink
                className="ion-text-center"
                to={journeyRoute({
                  journeyId: popoverState.journey?.id ?? "",
                  linkAfterJourney: "/messages",
                  myExpert
                })}
              >
                <FormattedMessage id="pages.expert.journeys.popoverTitles.sendMessage" />
              </InternalLink>
            </div>
            <div className={styles.popoverItem}>
              <InternalLink
                className="ion-text-center"
                to={journeyRoute({
                  journeyId: popoverState.journey?.id ?? "",
                  linkAfterJourney: "/time-logs",
                  myExpert
                })}
              >
                <FormattedMessage id="pages.expert.journeys.popoverTitles.logTime" />
              </InternalLink>
            </div>
            <div className={styles.popoverItem}>
              <InternalLink
                className="ion-text-center"
                to={journeyRoute({ journeyId: popoverState.journey?.id ?? "", linkAfterJourney: "", myExpert })}
              >
                <FormattedMessage id="pages.expert.journeys.popoverTitles.openJourney" />
              </InternalLink>
            </div>
            <div className={styles.popoverItem}>
              <div className={styles.flex}>
                <ButtonLink className="ion-text-center" onClick={handleExpertArchivedClick}>
                  {popoverState.journey?.expertArchived ? (
                    <FormattedMessage id="pages.expert.journeys.popoverTitles.unarchiveJourney" />
                  ) : (
                    <FormattedMessage id="pages.expert.journeys.popoverTitles.archiveJourney" />
                  )}
                </ButtonLink>
                <div className={styles.popoverItemIcon}>
                  <TableHeaderExtraInfo
                    descriptionKey="pages.expert.journeys.archive.description"
                    linkKey="pages.expert.journeys.archive.linkTitle"
                    onOpen={archiveDefinitionOpener.open}
                    titleKey="pages.expert.journeys.archive.title"
                  />
                </div>
              </div>
            </div>
            <div className={styles.popoverItem}>
              <ButtonLink className="ion-text-center" onClick={handleAddJourneyClick}>
                <FormattedMessage id="pages.expert.journeys.popoverTitles.addJourney" />
              </ButtonLink>
            </div>
            {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager && (
              <div className={styles.popoverItem}>
                <ButtonLink className="ion-text-center" onClick={handleJourneyReassign}>
                  <FormattedMessage id="pages.expert.journeys.popoverTitles.reassignClient" />
                </ButtonLink>
              </div>
            )}
          </div>
        </IonPopover>
      </div>
      <div className={styles.pageTitle}>
        <PageTitle imageName="peopleCoffeeLight" title={pageTitle} />
      </div>
      <ExpertJourneysFilters
        archiveDefinitionOpener={archiveDefinitionOpener}
        expertFirstName={getExpertQuery.data?.expert.firstName}
        filters={filters}
        myJourneys={myJourneys}
        onChange={handleFilterChange}
      />
      <IonContent>
        <HandleQuery query={getExpertJourneysQuery}>
          {data => (
            <>
              <div className={styles.journeyCount}>
                <FormattedMessage
                  id="pages.expert.journeys.journeyCount"
                  values={{ count: data?.expertJourneys.totalCount }}
                />
              </div>
              <Table<ExpertJourneysSortByEnum>
                fetchMore={getExpertJourneysQuery.fetchMore}
                headerKey="pages.expert.journeys.tableHeadings"
                headers={tableHeaders}
                pageInfo={data?.expertJourneys.pageInfo}
                setSortBy={setSortBy}
                setSortDirection={setSortDirection}
                sortBy={sortBy}
                sortDirection={sortDirection}
              >
                {/* filters journeys without a care recipient, unless they are the primary journey */}
                {journeys.map(journey => (
                  <tr key={journey.id}>
                    <td>
                      <Link
                        className={styles.unreadMessageWrapper}
                        to={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/messages", myExpert })}
                      >
                        <Icon
                          color="gray-100"
                          hoverContent={<FormattedMessage id="pages.expertJourney.messages.messagesExplainer" />}
                          icon="comments"
                          size="small"
                        />
                        {unreadCountsByJourneyId[journey.id] !== undefined &&
                          unreadCountsByJourneyId[journey.id] > 0 && (
                            <div className={styles.unreadMessageBadge}>{unreadCountsByJourneyId[journey.id]}</div>
                          )}
                      </Link>
                    </td>
                    <td>{!journeyOpenedAt(journey) && <Icon color="secondary-500" icon="star" />}</td>
                    <td>
                      <div className={styles.bigText}>
                        {journey.owner.lastName}
                        <FormattedMessage id="dictionary.punctuation.comma" />
                        <Nbsp />
                        {journey.owner.firstName}
                      </div>
                      <div className={styles.subHead}>
                        {journey.owner.accessType === ClientAccessTypeEnum.EMPLOYER_SPONSORED ? (
                          <>{journey.owner.employer?.name}</>
                        ) : (
                          <FormattedMessage id="dictionary.selfPaid" />
                        )}
                      </div>
                      {journey.owner.location.countryCode ||
                        (journey.owner.timezone && (
                          <div className={styles.subHead}>
                            {countryNameForCode(journey.owner.location.countryCode)}
                            {journey.owner.location.countryCode && journey.owner.timezone && (
                              <>
                                <FormattedMessage id="dictionary.punctuation.comma" />
                                <Nbsp />
                              </>
                            )}
                            {journey.owner.timezone && <>{timezoneAbbreviation(journey.owner.timezone)}</>}
                          </div>
                        ))}
                    </td>
                    <td>
                      <InternalLink
                        className={styles.bigText}
                        data-test="journey-route"
                        to={journeyRoute({ journeyId: journey.id, linkAfterJourney: "", myExpert })}
                      >
                        {getCareProfileName({ intl, journey })}
                      </InternalLink>
                      <div className={styles.subHead}>
                        {journey.ownerRole?.relationship?.relationshipOption &&
                          journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.other && (
                            <>
                              <FormattedMessage
                                id={`models.clientRole.relationship.${journey.ownerRole?.relationship?.relationshipOption}`}
                              />
                              {journey.careProfile.age && (
                                <>
                                  <FormattedMessage id="dictionary.punctuation.comma" />
                                  <Nbsp />
                                </>
                              )}
                            </>
                          )}
                        {journey.ownerRole?.relationship?.other && (
                          <>
                            {journey.ownerRole.relationship.other}
                            {journey.careProfile.age && (
                              <>
                                <FormattedMessage id="dictionary.punctuation.comma" />
                                <Nbsp />
                              </>
                            )}
                          </>
                        )}
                        {journey.careProfile.age && (
                          <FormattedMessage id={`models.careProfile.ageOptions.${journey.careProfile.age}`} />
                        )}
                      </div>
                      {journey.careProfile.location.countryCode && (
                        <div className={styles.subHead}>
                          {countryNameForCode(journey.careProfile.location.countryCode)}
                        </div>
                      )}
                    </td>
                    <td>
                      <FormattedMessage id={`models.journey.status.${journey.status}`} />
                    </td>
                    <td>
                      {journey.lastExpertContact && <DateStamp showWeekday={false} value={journey.lastExpertContact} />}
                    </td>
                    <td>
                      {journey.lastClientContact && <DateStamp showWeekday={false} value={journey.lastClientContact} />}
                    </td>
                    <td>
                      <DateStamp showWeekday={false} value={journey.createdAt} />
                    </td>
                    <td>
                      {journey.carePlanUpdatedAt && journey.carePlanPublishedAt && (
                        <DateStamp showWeekday={false} value={journey.carePlanUpdatedAt} />
                      )}
                    </td>
                    <td className={styles.lessPadding}>
                      <ExpertRoleBoxes expertRoles={journey.expertRoles} />
                    </td>
                    <td>
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.expertJourneysPopoverMenu"
                        icon="ellipsisV"
                        onClick={event => {
                          setPopoverState({ event: event as unknown as Event, journey, open: true });
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </HandleQuery>
      </IonContent>
      <Modal
        opener={archiveModalOpener}
        ref={archiveModalRef}
        title={intl.formatMessage({ id: "pages.expert.journeys.archiveModal.title" })}
      >
        {() => (
          <ArchiveJourneyForm
            journey={modifyingJourney}
            onCancel={archiveModalOpener.close}
            onError={handleArchiveError}
            onSuccess={archiveModalOpener.close}
          />
        )}
      </Modal>
      <Modal
        opener={expertAssignmentOpener}
        ref={expertAssignmentModalRef}
        title={intl.formatMessage({ id: "pages.expert.journeys.expertAssignment.title" })}
      >
        {() => {
          if (modifyingJourney) {
            return (
              <ExpertAssignmentForm
                journey={modifyingJourney}
                onCancel={expertAssignmentOpener.close}
                onError={handleAssignmentError}
                onSuccess={expertAssignmentOpener.close}
                refetch={getExpertJourneysQuery.refetch}
              />
            );
          }
          return null;
        }}
      </Modal>
      <Modal
        opener={newJourneyModalOpener}
        ref={newJourneyModalRef}
        title={intl.formatMessage({ id: "pages.expert.journeys.newJourneyModal.title" })}
      >
        {({ modalDisplayed }) => {
          if (newJourneyClientId && modifyingJourney?.owner) {
            if (
              hasBasicPackage(modifyingJourney) &&
              !clientHasUnattachedCarePlanningPackage &&
              modifyingJourney.owner.timeBalance <= 0
            ) {
              return (
                <div>
                  <FormattedMessage id="pages.expert.journeys.zeroJourneyModal.zeroBalanceBasic" />
                </div>
              );
            }
            if (modifyingJourney.owner.timeBalance <= 0 && !clientHasUnattachedCarePlanningPackage) {
              return (
                <div>
                  <FormattedMessage id="pages.expert.journeys.zeroJourneyModal.zeroBalance" />
                </div>
              );
            }
            return (
              <NewJourneyForm
                autofocus={modalDisplayed}
                clientId={newJourneyClientId}
                onCancel={newJourneyModalOpener.close}
                onError={handleNewJourneyError}
                onSuccess={newJourneyModalOpener.close}
              />
            );
          }
          return null;
        }}
      </Modal>
      <JourneyStatusModal opener={statusDefinitionOpener} />
      <JourneyArchiveModal opener={archiveDefinitionOpener} />
    </>
  );
};

export default ExpertJourneysList;
